import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readPresentation, shapesOfType } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";
import { criterion, fromCriteria } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { DIAGRAMMTYP, istRaeumlich, MAKRO_DIAGRAMME } from "../makros";

export const lektionDiagramme: Lesson = {
  id: "diagramme",
  title: "Diagramme",
  summary: "Ein Diagramm zeigt eine Aussage – nicht alle Daten, die man hat.",
  durationMinutes: 11,
  learningGoals: [
    "Den passenden Diagrammtyp zur Aussage wählen",
    "Diagramme über den Inhaltsplatzhalter einfügen",
    "Den Unterschied zwischen eingebetteten und verknüpften Daten kennen",
  ],
  steps: [
    {
      id: "diagrammtyp",
      title: "Der Diagrammtyp folgt der Aussage",
      explanation: [
        "Die Wahl des Diagrammtyps ist keine Geschmacksfrage, sondern folgt daraus, was " +
          "gezeigt werden soll. Vier Fälle decken fast alles ab.",
        "Eine Entwicklung über die Zeit gehört in ein Liniendiagramm – die Linie zeigt " +
          "den Verlauf, und das Auge folgt ihr von selbst. Ein Vergleich zwischen " +
          "wenigen Kategorien gehört in ein Säulen- oder Balkendiagramm; bei langen " +
          "Kategorienamen ist das liegende Balkendiagramm besser lesbar. Ein Anteil am " +
          "Ganzen kann ein Kreisdiagramm sein – aber nur bei wenigen Segmenten, weil das " +
          "Auge Winkel schlecht vergleicht. Ein Zusammenhang zwischen zwei Größen gehört " +
          "in ein Punktdiagramm.",
        "Was in keinem Fall hilft, sind 3D-Effekte. Eine perspektivisch gekippte Säule " +
          "ist nicht schöner, sondern messbar falscher: Die vordere Säule wirkt größer " +
          "als die hintere, obwohl sie denselben Wert hat. Dasselbe gilt für Schatten und " +
          "Verläufe – sie kosten Genauigkeit und bringen nichts ein.",
        "Ein Diagramm trägt genau eine Aussage. Wenn du beim Erklären das Wort „und“ " +
          "brauchst, sind es zwei Diagramme.",
      ],
      keyPoints: [
        "Verlauf → Linie, Vergleich → Säule oder Balken, Anteil → Kreis, Zusammenhang → Punkte.",
        "Kreisdiagramme nur bei wenigen Segmenten.",
        "Keine 3D-Effekte – sie verzerren die Wahrnehmung der Werte.",
      ],
      task: {
        instruction: "Füge auf einer Folie ein Diagramm ein.",
        hint: "Über das Diagrammsymbol im leeren Inhaltsplatzhalter – dann sitzt es gleich richtig.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Diagramm, Tabelle, Bild)",
              "1.4",
              "Sieh selbst nach, ob auf einer Folie ein Diagramm liegt.",
            );
          }

          const diagramme = shapesOfType(snapshot, "Chart");
          if (diagramme.length === 0) {
            return fail(
              "In der Präsentation ist noch kein Diagramm eingefügt.",
              "Im leeren Inhaltsplatzhalter auf das Diagrammsymbol klicken und einen Typ wählen.",
            );
          }
          return pass(
            `Diagramm gefunden – auf Folie ${diagramme[0].slideIndex + 1}.` +
              " Welcher Typ es ist, verrät Office.js nicht – das prüft im nächsten Schritt ein Prüfmakro.",
          );
        },
      },
    },
    {
      id: "beschriftung",
      title: "Beschriften statt raten lassen",
      explanation: [
        "Ein Diagramm ohne Beschriftung zwingt das Publikum zum Raten. Drei Angaben " +
          "müssen immer dastehen: was die Achsen bedeuten, in welcher Einheit gemessen " +
          "wird und woher die Zahlen stammen.",
        "Die Achsenbeschriftung und der Diagrammtitel kommen über das Pluszeichen rechts " +
          "neben dem markierten Diagramm („Diagrammelemente“). Die Einheit gehört in die " +
          "Achsenbeschriftung – „Umsatz in Mio. €“ statt nur „Umsatz“.",
        "Datenbeschriftungen an den einzelnen Punkten sind ein zweischneidiges Mittel. " +
          "Bei wenigen Werten sind sie hilfreich, weil man die Zahl nicht an der Achse " +
          "ablesen muss. Bei vielen Werten machen sie das Diagramm unlesbar. Faustregel: " +
          "Entweder Datenbeschriftungen oder Gitternetzlinien – nicht beides.",
        "Die Quelle steht klein unter dem Diagramm. Genau dafür ist ein freies Textfeld " +
          "der richtige Ort, wie im Modul „Platzhalter statt Textfelder“ beschrieben: Es " +
          "gehört räumlich zum Diagramm und wird zusammen mit ihm gruppiert.",
      ],
      keyPoints: [
        "Pluszeichen am markierten Diagramm öffnet die Diagrammelemente.",
        "Einheit in die Achsenbeschriftung: „Umsatz in Mio. €“.",
        "Entweder Datenbeschriftungen oder Gitternetzlinien, nicht beides.",
      ],
      task: {
        instruction:
          "Gib deinem Diagramm einen Titel und Datenbeschriftungen oder eine Legende – und " +
          "verwende keinen räumlichen (3D-)Diagrammtyp.",
        hint: "Diagramm anklicken, + → Diagrammtitel, + → Datenbeschriftungen. Den Typ ändert „Diagrammentwurf → Diagrammtyp ändern“.",
        macro: MAKRO_DIAGRAMME,
        check: () =>
          pruefeMitMakro(MAKRO_DIAGRAMME, (bericht) => {
            const diagramme = [...bericht.gruppe("diagramm").values()];
            if (diagramme.length === 0) {
              return fail("In der Präsentation ist noch kein Diagramm.", "Füge zuerst über den Inhaltsplatzhalter ein Diagramm ein.");
            }
            const typen = diagramme.map((d) => Number(d.get("typ")));
            const raeumlich = typen.filter(istRaeumlich);
            const mitTitel = diagramme.filter((d) => d.get("titel") === "1" && (d.get("titeltext") ?? "").trim().length > 0);
            const lesbar = diagramme.filter((d) => d.get("beschriftung") === "1" || d.get("legende") === "1");
            return fromCriteria(
              [
                criterion(
                  `Diagrammtyp: ${typen.map((t) => DIAGRAMMTYP[t] ?? `Typ ${t}`).join(", ")} – ohne 3D-Effekt`,
                  raeumlich.length === 0,
                ),
                criterion("Jedes Diagramm hat einen Titel", mitTitel.length === diagramme.length),
                criterion("Jedes Diagramm hat Datenbeschriftungen oder eine Legende", lesbar.length === diagramme.length),
              ],
              {
                success: "Typ, Titel und Beschriftung stimmen – niemand muss raten.",
                failure: "Das Diagramm ist noch nicht vollständig beschriftet.",
                hint: "Räumliche Säulen, Kegel oder Pyramiden verzerren die Werte – lieber flache Säulen.",
              },
            );
          }),
      },
    },
    {
      id: "daten",
      title: "Woher die Zahlen kommen",
      explanation: [
        "Wird ein Diagramm in PowerPoint eingefügt, öffnet sich ein kleines " +
          "Excel-Fenster mit Beispieldaten. Diese Daten leben danach in der " +
          "PowerPoint-Datei – sie sind eingebettet. Über „Diagrammentwurf“ → „Daten " +
          "bearbeiten“ kommt man jederzeit wieder heran.",
        "Der zweite Weg ist, ein Diagramm aus einer Excel-Datei zu kopieren und in " +
          "PowerPoint einzufügen. Beim Einfügen gibt es dann die Wahl zwischen " +
          "„Arbeitsmappe einbetten“ und „Daten verknüpfen“. Eingebettet heißt: " +
          "unabhängig, aber auch nicht mehr aktuell. Verknüpft heißt: aktualisiert sich, " +
          "solange die Excel-Datei am selben Ort liegt.",
        "In der Praxis ist die Verknüpfung häufiger ein Problem als eine Hilfe. Sobald " +
          "die Präsentation verschickt oder die Excel-Datei verschoben wird, ist die " +
          "Verbindung tot, und PowerPoint zeigt beim Öffnen eine Warnung. Für " +
          "Präsentationen, die aus der Hand gehen, ist Einbetten die richtige Wahl. " +
          "Verknüpfen lohnt sich nur bei einem Bericht, der regelmäßig mit denselben " +
          "Dateien aktualisiert wird.",
        "Wichtig für den Datenschutz: Eine eingebettete Arbeitsmappe enthält alles, was " +
          "darin steht – auch Tabellenblätter, die im Diagramm nicht sichtbar sind. Vor " +
          "dem Versand lohnt sich ein Blick in „Daten bearbeiten“.",
      ],
      keyPoints: [
        "Diagrammentwurf → Daten bearbeiten öffnet die eingebettete Arbeitsmappe.",
        "Für Präsentationen, die verschickt werden: einbetten, nicht verknüpfen.",
        "Die eingebettete Arbeitsmappe enthält auch unsichtbare Tabellenblätter.",
      ],
    },
  ],
};
